import { extendTheme } from '@chakra-ui/react';
import { colors } from './colors';
import { textStyles } from './textStyles';
import { buttonStyle } from './button';
import { badgeStyle } from './badge';
import { modalStyle } from './modal';
import { defineStyleConfig } from '@chakra-ui/styled-system';

const headingTheme = defineStyleConfig({
  variants: {
    headingHero: textStyles.headlineHero,
    headingHeroSmall: textStyles.headlineHeroSmall,
    headingLarge: textStyles.headlineLarge,
    headingMedium: textStyles.headlineMedium,
    headingSmall: textStyles.headlineSmall,
  },
  baseStyle: {
    fontFamily: 'Noto Sans, Noto Sans Arabic, sans-serif',
  },
});

const textTheme = defineStyleConfig({
  variants: {
    bodyLargeRegular: textStyles.bodyLargeRegular,
    bodyLargeSemiBold: textStyles.bodyLargeSemiBold,
    bodySmallRegular: textStyles.bodySmallRegular,
    bodySmallSemibold: textStyles.bodySmallSemibold,
    bodyLargestRegular: textStyles.bodyLargestRegular,
    subtitleLarge: textStyles.subtitleLarge,
    subtitleSmallRegular: textStyles.subtitleSmallRegular,
    subtitleSmall: textStyles.subtitleSmall,
    caption: textStyles.caption,
  },
});

const badgeTheme = defineStyleConfig({
  variants: {
    primary: badgeStyle.primary,
    red: badgeStyle.red,
  },
});

const buttonTheme = defineStyleConfig({
  variants: {
    primaryLarge: buttonStyle.primaryLarge,
    primaryMedium: buttonStyle.primaryMedium,
    secondaryLarge: buttonStyle.secondaryLarge,
    secondaryMedium: buttonStyle.secondaryMedium,
    brandLarge: buttonStyle.brandLarge,
    brandMedium: buttonStyle.brandMedium,
    ghostLarge: buttonStyle.ghostLarge,
    ghostMedium: buttonStyle.ghostMedium,
    ghostNoPaddingLarge: buttonStyle.ghostNoPaddingLarge,
    ghostNoPaddingMedium: buttonStyle.ghostNoPaddingMedium,
    ghostWhiteLarge: buttonStyle.ghostWhiteLarge,
    ghostWhiteMedium: buttonStyle.ghostWhiteMedium,
    ghostWhiteNoPaddingLarge: buttonStyle.ghostWhiteNoPaddingLarge,
    ghostWhiteNoPaddingMedium: buttonStyle.ghostWhiteNoPaddingMedium,
    ghostB2bNavNoPaddingMedium: buttonStyle.ghostB2bNavNoPaddingMedium,
    b2bPrimaryLarge: buttonStyle.b2bPrimaryLarge,
    b2bLandingHeroPrimaryLarge: buttonStyle.b2bLandingHeroPrimaryLarge,
    b2bSecondaryLarge: buttonStyle.b2bSecondaryLarge,
    b2bRegistrationPrimaryLarge: buttonStyle.b2bRegistrationPrimaryLarge,
    b2bRegistrationSecondaryLarge: buttonStyle.b2bRegistrationSecondaryLarge,
    b2bRegistrationCancelTextLarge: buttonStyle.b2bRegistrationCancelTextLarge,
    navRegisterEventMedium: buttonStyle.navRegisterEventMedium,
    // Variants only exist once they are listed here. `coralLarge` was declared in `button.ts` but
    // never registered, so the checkout purchase CTA silently fell back to Chakra's default button
    // — 40px tall, 6px radius, transparent — for as long as it has been on the page.
    coralCta: buttonStyle.coralCta,
    danger: buttonStyle.danger,
  },
});

export const theme = extendTheme({
  styles: {
    global: {
      'html, body': {
        color: colors.dark.D900,
        minHeight: '100vh',
        height: '100vh',
        fontFamily: 'Noto Sans, Noto Sans Arabic, sans-serif',
      },
      body: {
        bg: colors.BG100,
        overflowX: 'hidden',
      },
    },
  },
  colors: colors,
  /**
   * `thmanyah` is the V2 brand face (loaded in {@link Fonts}). Kept as a token so pages opting into
   * it reference one definition instead of repeating the stack — the duplication TZ-CO-19 flagged.
   * `Noto Sans` stays in the fallback chain for Latin runs such as promo codes.
   */
  fonts: {
    thmanyah: "'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif",
  },
  textStyles: textStyles,
  components: {
    Heading: headingTheme,
    Button: buttonTheme,
    Link: {
      baseStyle: {
        '&:hover': { textDecoration: 'none', color: colors.green.G0 },
      },
    },
    Badge: badgeTheme,
    CloseButton: {
      baseStyle: {
        color: 'dark.D400',
        left: '8px',
        borderRadius: '10px',
      },
    },
    Modal: {
      baseStyle: modalStyle,
    },
    Text: textTheme,
  },
});
